/**
 * Jurídico: formalização dos instrumentos e registro contratual gerado da solicitação (ADR 0056).
 * Diferencia o atendimento concluído do instrumento formalizado e diz que registro contratual
 * cada modalidade gera.
 */
import type {
  CONTRACT_CATEGORIES,
  CONTRACT_KINDS,
  CONTRACT_PERIODICITIES,
} from './legal-contracts.ts';
import { SERVICE_SIGNING_POWER } from './representatives.ts';

/**
 * Resultado da conclusão: instrumento assinado e formalizado; análise concluída (pareceres,
 * consultas e orientações, com resposta registrada); instrumento encerrado sem assinatura.
 */
export const LEGAL_OUTCOMES = ['formalized', 'answered', 'closed_unformalized'] as const;
export type LegalOutcome = (typeof LEGAL_OUTCOMES)[number];

/**
 * Termos e autorizações (F14 a F16): formalizam, mas a exigência da composição de assinaturas
 * continua a de hoje até a decisão D-08 da Especificação v2.
 */
export const LEGAL_TERM_SERVICES: readonly string[] = ['F14', 'F15', 'F16'];

/** Instrumento (exige assinatura), termo ou consulta (parecer, orientação, outras). */
export function legalServiceNature(code: string): 'instrument' | 'term' | 'consultation' {
  if (SERVICE_SIGNING_POWER[code]) return 'instrument';
  if (LEGAL_TERM_SERVICES.includes(code)) return 'term';
  return 'consultation';
}

/**
 * Revisão de contrato de terceiro (F33): termina só com a análise (sem contratação) ou vira
 * instrumento formalizado pelo fluxo contratual, com assinaturas (ADR 0063).
 */
export const LEGAL_REVIEW_SERVICES: readonly string[] = ['F33'];

/** Resultados possíveis na conclusão de cada modalidade. */
export function legalAllowedOutcomes(code: string): LegalOutcome[] {
  if (LEGAL_REVIEW_SERVICES.includes(code))
    return ['answered', 'formalized', 'closed_unformalized'];
  return legalServiceNature(code) === 'consultation'
    ? ['answered']
    : ['formalized', 'closed_unformalized'];
}

type Category = (typeof CONTRACT_CATEGORIES)[number];
type Kind = (typeof CONTRACT_KINDS)[number];

const MAIN_CATEGORY: Record<string, Category> = {
  F01: 'artist',
  F02: 'artist',
  F03: 'influencer',
  F04: 'influencer',
  F05: 'supply',
  F06: 'supply',
  F07: 'services',
  F08: 'partnership',
  F09: 'licensing',
  F10: 'other',
  F11: 'nda',
  F12: 'nda',
  F13: 'nda',
};

// Lista local (importar o valor de legal-contracts criaria um ciclo com legal.ts); o tipo garante que
// só há categorias válidas.
const CATEGORY_CHOICES: ReadonlySet<string> = new Set<Category>([
  'services',
  'rental',
  'saas',
  'partnership',
  'licensing',
  'supply',
  'artist',
  'influencer',
  'nda',
  'other',
]);

/** F33: o tipo de documento recebido define a categoria do contrato, quando vira contratação. */
const F33_CATEGORY: Record<string, Category> = {
  services: 'services',
  supply: 'supply',
  licensing: 'licensing',
  partnership: 'partnership',
  nda: 'nda',
  saas: 'saas',
};

/** F21: o tipo informado no formulário define a categoria do contrato. */
const F21_CATEGORY: Record<string, Category> = {
  services: 'services',
  licensing: 'licensing',
  partnership: 'partnership',
  purchase: 'supply',
  saas: 'saas',
};

/**
 * Registro contratual que a formalização gera. Aditivo (F19), distrato (F20) e revisão ou
 * renovação (F22) ficam ligados ao contrato original do campo "contrato". Procuração e revogação
 * (F17, F18) e os termos (F14 a F16) não geram contrato (decisões D-08 e D-10).
 */
export function legalContractPlan(
  code: string,
  fields: Record<string, unknown>,
): { kind: Kind; category: Category | null } | null {
  const plan = basePlan(code, fields);
  // Categoria informada no formulário (campo categoria_contrato), quando o Jurídico o incluir: vale
  // sobre a da modalidade no contrato principal. Ex.: fornecedor de software vira SaaS, e a TI o
  // encontra para ligar à contratação (auditoria de 27/10, Onda 2).
  const chosen = fields.categoria_contrato;
  if (plan?.kind === 'main' && typeof chosen === 'string' && CATEGORY_CHOICES.has(chosen)) {
    return { kind: 'main', category: chosen as Category };
  }
  return plan;
}

function basePlan(
  code: string,
  fields: Record<string, unknown>,
): { kind: Kind; category: Category | null } | null {
  if (MAIN_CATEGORY[code]) return { kind: 'main', category: MAIN_CATEGORY[code] };
  if (code === 'F21') {
    const t = typeof fields.tipo_contrato === 'string' ? fields.tipo_contrato : '';
    return { kind: 'main', category: F21_CATEGORY[t] ?? 'other' };
  }
  if (code === 'F33') {
    // Revisão ligada a um contrato cadastrado: a formalização é um aditivo dele (sem duplicar).
    const ref = fields.contrato_vinculado as { contractId?: string } | undefined;
    if (ref?.contractId) return { kind: 'amendment', category: null };
    const t = typeof fields.tipo_instrumento === 'string' ? fields.tipo_instrumento : '';
    return { kind: 'main', category: F33_CATEGORY[t] ?? 'other' };
  }
  if (code === 'F19') return { kind: 'amendment', category: null };
  if (code === 'F20') return { kind: 'termination', category: null };
  if (code === 'F22') {
    return { kind: fields.tipo === 'renewal' ? 'renewal' : 'amendment', category: null };
  }
  return null;
}

/** Periodicidade da solicitação → a do contrato (as que não existem lá viram "outra"). */
export function contractPeriodicityOf(
  p: string | null | undefined,
): (typeof CONTRACT_PERIODICITIES)[number] | null {
  if (!p || p === 'not_applicable') return null;
  if (p === 'per_delivery') return 'on_demand';
  if (['one_time', 'monthly', 'quarterly', 'semiannual', 'yearly'].includes(p)) {
    return p as (typeof CONTRACT_PERIODICITIES)[number];
  }
  return 'other';
}

/**
 * Origem da minuta (D-14, decisão parcial): quem pede indica nas modalidades que geram um contrato
 * principal (e na elaboração de outros contratos); a equipe jurídica pode corrigir, com motivo.
 * Quando cada origem é aceita ainda não foi decidido: a origem não bloqueia nem exige nada.
 */
export const LEGAL_DRAFT_ORIGINS = [
  'standard_template',
  'counterparty_draft',
  'new_draft',
] as const;
export type LegalDraftOrigin = (typeof LEGAL_DRAFT_ORIGINS)[number];

/** A modalidade pergunta a origem da minuta? (Revisão de contrato de terceiro já é a da outra parte.) */
export function legalUsesDraftOrigin(code: string): boolean {
  return code in MAIN_CATEGORY || code === 'F21';
}

/** Situação de cada ponto obrigatório na revisão da minuta da outra parte (D-14). */
export const LEGAL_REVIEW_STATUSES = ['meets', 'adjusted', 'accepted_with_risk'] as const;
export type LegalReviewStatus = (typeof LEGAL_REVIEW_STATUSES)[number];

/**
 * Pontos que o Jurídico confere em toda minuta de terceiro (lista inicial provisória; cada modalidade
 * pode ter a sua em legal.service.review_points).
 */
export const LEGAL_REVIEW_POINTS = [
  'confidentiality',
  'intellectual_property',
  'data_protection',
  'anticorruption',
  'liability',
  'penalties',
  'termination',
  'governing_law',
] as const;
